// Event-driven audio: translate game event ids into spatialised one-shot playback.
//
// Each `post(eventId, payload)` call: checks a per-event cooldown, then plays the registered
// buffer through a pooled voice on the event's bus, panned by an injected listener. The engine is
// a pure function of the events it is told about — it never reads game state. That keeps replays
// sounding identical (same events at same ticks → same audio).
//
// `Math.random` (pitch jitter) and `fetch` (buffer loading) are fine here: this is post-sim
// presentation, not deterministic gameplay. The engine is generic over the game's event id union.

import type { Point2 } from "@neoweb/core";
import { type BusGraph, type BusId, type BusTopology, createBusGraph } from "./buses.js";
import { AudioContextManager } from "./context-manager.js";
import { VoicePool } from "./voice-pool.js";

export type AudioEventDef = {
  bus: BusId;
  url: string;
  baseVolume: number; // linear gain (0..1+)
  pitchJitter: number; // ± fraction
  cooldownMs: number; // suppress repeat fire within this window
};

export type AudioEventPayload = {
  position?: Point2; // world position; panned via the listener
  intensity?: number; // [0, 1] — drives volume and pitch jitter
  pan?: number; // explicit pan override [-1, 1]
};

/** Maps a world position to a stereo pan in [-1, 1]. Injected by the app (it knows the camera). */
export type PanListener = (pos: Point2) => number;

export interface GameAudio<EventId extends string = string> {
  unlock(): Promise<void>;
  registerEvent(id: EventId, def: AudioEventDef): Promise<void>;
  post(id: EventId, payload?: AudioEventPayload): void;
  setListener(listener: PanListener | null): void;
}

export class AudioEngine<EventId extends string = string> implements GameAudio<EventId> {
  private ctxMgr = new AudioContextManager();
  private buses: BusGraph | null = null;
  private readonly pool: VoicePool;
  private buffers = new Map<EventId, AudioBuffer>();
  private defs = new Map<EventId, AudioEventDef>();
  private lastFiredMs = new Map<EventId, number>();
  // Stable listener function set once via setListener — invoked directly on each spatial event.
  private listener: PanListener | null = null;

  constructor(
    private readonly topology: BusTopology,
    opts?: { voices?: number; maxPerBus?: number },
  ) {
    this.pool = new VoicePool(opts?.voices, opts?.maxPerBus);
  }

  async unlock(): Promise<void> {
    const ctx = this.ctxMgr.ensure();
    if (this.buses === null) {
      this.buses = createBusGraph(ctx, this.topology);
      this.pool.initialize(ctx, this.buses);
    }
    if (ctx.state === "suspended") {
      await ctx.resume();
    }
  }

  async registerEvent(id: EventId, def: AudioEventDef): Promise<void> {
    this.defs.set(id, def);
    const buffer = await this.loadBuffer(def.url);
    if (buffer !== null) this.buffers.set(id, buffer);
  }

  post(id: EventId, payload?: AudioEventPayload): void {
    const def = this.defs.get(id);
    const buffer = this.buffers.get(id);
    const ctx = this.ctxMgr.context();
    if (def === undefined || buffer === undefined || ctx === null || this.buses === null) return;

    // Cooldown gate.
    const now = ctx.currentTime * 1000;
    const last = this.lastFiredMs.get(id) ?? Number.NEGATIVE_INFINITY;
    if (now - last < def.cooldownMs) return;
    this.lastFiredMs.set(id, now);

    const pitchVariance = (Math.random() * 2 - 1) * def.pitchJitter;
    const intensity = def.baseVolume * (payload?.intensity ?? 1);

    let pan = 0;
    if (payload?.pan !== undefined) {
      pan = payload.pan;
    } else if (payload?.position !== undefined && this.listener !== null) {
      pan = this.listener(payload.position);
    }

    this.pool.play({
      busId: def.bus,
      buffer,
      intensity,
      pan,
      pitchVariance,
      startedAtSec: ctx.currentTime,
    });
  }

  setListener(listener: PanListener | null): void {
    this.listener = listener;
  }

  private async loadBuffer(url: string): Promise<AudioBuffer | null> {
    const ctx = this.ctxMgr.context();
    if (ctx === null) return null;
    try {
      const res = await fetch(url);
      if (!res.ok) return null;
      const data = await res.arrayBuffer();
      return await ctx.decodeAudioData(data);
    } catch {
      return null;
    }
  }
}
